import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { Menu, Moon, RotateCcw, X } from "lucide-react";
import { api } from "@/lib/apiClient";
import { PUBLIC_HOME } from "@/lib/native";
import { useAuth } from "@/context/AuthContext";
import { navTabs } from "@/components/BottomNav";

const LINKS = [["/about", "About", "about"], ["/editorial-policy", "Editorial Policy", "editorial"], ["/contact", "Contact", "contact"],
  ["/privacy", "Privacy Policy", "privacy"], ["/terms", "Terms of Use", "terms"], ["/support", "Support", "support"], ["/creators", "Creator Program", "creators"]];

const NAV = [["/readings", "Readings", "readings"], ["/compatibility", "Compatibility", "compatibility"]];
const MOON_TIPS = {
  "New Moon": "good for setting intentions and quiet fresh starts.",
  "Waxing Crescent": "good for taking the first small step on a new plan.",
  "First Quarter": "good for decisions and pushing past a hurdle.",
  "Waxing Gibbous": "good for refining, editing and fine-tuning your work.",
  "Full Moon": "good for celebrating, letting feelings out and seeing things clearly.",
  "Waning Gibbous": "good for sharing what you've learned and saying thank you.",
  "Last Quarter": "good for clearing clutter and letting go of what's finished.",
  "Waning Crescent": "good for rest, reflection and an early night.",
};

function MenuList({ links, pathname, close, small }) {
  return (
    <ul className="py-2">
      {links.map(([to, label, id]) => (
        <li key={id}>
          <Link to={to} data-testid={`menu-link-${id}`} onClick={close}
            className={`block px-6 ${small ? "py-2.5 text-base" : "py-3 text-lg"} transition-colors hover:bg-white/5 ${pathname === to ? "gold-text" : small ? "text-[#C9C2B6]" : "text-[#E8DCC4]"}`}>
            {label}
          </Link>
        </li>
      ))}
    </ul>
  );
}

export default function SiteMenu({ floating = false }) {
  const [open, setOpen] = useState(false);
  const [moon, setMoon] = useState(null);
  const [retros, setRetros] = useState([]);
  const user = useAuth()?.user;
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return;
    api.get("/public/transits/moon").then(({ data }) => setMoon(data.bodies?.[0] || null)).catch(() => {});
    api.get("/public/retrogrades").then(({ data }) => setRetros((data.planets || []).filter((r) => r.retrograde))).catch(() => {});
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  const pos = floating ? "absolute left-3 z-30 top-[calc(env(safe-area-inset-top)+0.75rem)]" : "-ml-2 shrink-0";
  return (
    <>
      <button type="button" data-testid="site-menu-btn" aria-label="Open menu" aria-expanded={open} onClick={() => setOpen(true)}
        className={`${pos} w-10 h-10 flex items-center justify-center rounded-full text-[#D6D0C5] hover:text-starlight hover:bg-white/5 transition-colors`}>
        <Menu size={22} />
      </button>
      {typeof document !== "undefined" && createPortal(<>
      <div data-testid="site-menu-overlay" onClick={() => setOpen(false)} aria-hidden="true"
        className={`fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`} />
      <nav aria-label="Site information" data-testid="site-menu" aria-hidden={!open} inert={open ? undefined : ""}
        className={`fixed top-0 left-0 bottom-0 z-[61] w-72 max-w-[85vw] bg-[#0A0E18] border-r border-[#D4AF37]/25 safe-top flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="h-16 px-4 flex items-center justify-between border-b border-[#D4AF37]/15">
          <span className="flex items-center gap-2">
            <img src="/astra-mark-128.png" alt="" aria-hidden="true" className="w-8 h-8" />
            <span className="font-display text-lg tracking-[0.3em] text-starlight">ASTRA</span>
          </span>
          <button type="button" data-testid="site-menu-close" aria-label="Close menu" onClick={() => setOpen(false)}
            className="w-10 h-10 flex items-center justify-center rounded-full text-[#A7B0C0] hover:text-starlight">
            <X size={20} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto pb-[env(safe-area-inset-bottom)]">
          <Link to="/moon-calendar" data-testid="menu-moon-card" onClick={() => setOpen(false)}
            className="mx-4 mt-4 flex items-center gap-3 bg-[#0F1523] border border-[#D4AF37]/25 hover:border-[#D4AF37]/60 rounded-2xl px-4 py-3 transition-colors">
            <Moon size={20} className="gold-text shrink-0" />
            <span className="min-w-0">
              <span className="block text-[10px] uppercase tracking-[0.25em] gold-text">Today's Moon</span>
              <span data-testid="menu-moon-phase" className="block text-base text-starlight">{moon ? moon.phase : "Moon calendar"}</span>
              {moon && <span data-testid="menu-moon-detail" className="block text-xs text-[#A7B0C0]">In {moon.sign} · {moon.illumination}% lit</span>}
              {moon && MOON_TIPS[moon.phase] && <span data-testid="menu-moon-tip" className="block text-xs italic text-[#D6D0C5] mt-1">Tonight: {MOON_TIPS[moon.phase]}</span>}
            </span>
          </Link>
          {retros.length > 0 && (
            <div data-testid="menu-retro-notice" className="mx-4 mt-2 flex items-start gap-2 rounded-xl border border-[#D4AF37]/40 bg-[#2a2110]/60 px-3 py-2 text-xs text-[#E8DCC4]">
              <RotateCcw size={14} className="gold-text shrink-0 mt-0.5" />
              <span>Retrograde now:{" "}
                {retros.map((r, i) => (
                  <span key={r.planet}>{i > 0 && " · "}
                    <Link to={`/transits/${["Uranus", "Neptune"].includes(r.planet) ? "uranus-neptune" : r.planet.toLowerCase()}`} onClick={() => setOpen(false)}
                      data-testid={`menu-retro-${r.planet.toLowerCase()}`} className="gold-text underline-offset-2 hover:underline">{r.planet}</Link>
                  </span>
                ))}
              </span>
            </div>
          )}
          <MenuList links={user ? [...navTabs().map((t) => [t.to, t.label, t.testId.replace("nav-", "")]), NAV[1]] : [[PUBLIC_HOME, "Horoscopes", "horoscopes"], ...NAV]} pathname={pathname} close={() => setOpen(false)} />
          <div className="mx-6 border-t border-[#D4AF37]/15" />
          <MenuList links={LINKS} pathname={pathname} close={() => setOpen(false)} small />
        </div>
      </nav>
      </>, document.body)}
    </>
  );
}
